import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, MailOpen, Trash2, Phone, X } from 'lucide-react';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function AdminMessaggi() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API}/contact`);
      setMessages(res.data);
    } catch { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const markRead = async (m) => {
    try {
      await axios.put(`${API}/contact/${m.id}`, { read: true });
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/contact/${id}`); toast.success('Messaggio eliminato'); setDeleteId(null); setSelected(null); fetchAll(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  const openMessage = (m) => {
    setSelected(m);
    if (!m.read) markRead(m);
  };

  const filtered = messages.filter(m => {
    if (filter === 'unread') return !m.read;
    if (filter === 'read') return m.read;
    return true;
  });

  const fmtDate = (d) => new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="min-w-0 w-full">
      <div className="mb-5">
        <h2 className="font-heading text-xl font-bold text-gray-900">Messaggi</h2>
        <p className="font-body text-gray-500 text-xs mt-0.5">
          {messages.filter(m => !m.read).length} non letti · {messages.length} totali
        </p>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {[['all', 'Tutti'], ['unread', 'Non letti'], ['read', 'Letti']].map(([val, lbl]) => (
          <button
            key={val}
            onClick={() => setFilter(val)}
            className={`px-3 py-1.5 rounded-lg font-body text-xs font-medium transition-colors ${filter === val ? 'bg-primary text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="bg-white rounded-xl h-20 animate-pulse border border-gray-100" />)}</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((m) => (
            <div
              key={m.id}
              onClick={() => openMessage(m)}
              className={`bg-white rounded-xl px-5 py-4 border-2 cursor-pointer transition-all hover:shadow-sm ${!m.read ? 'border-primary/20 bg-primary/2' : 'border-gray-100'}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${!m.read ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400'}`}>
                    {m.read ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-body text-sm ${!m.read ? 'font-bold text-gray-900' : 'font-semibold text-gray-700'}`}>{m.name}</span>
                      <span className="font-body text-xs text-gray-400">{m.email}</span>
                      {!m.read && <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />}
                    </div>
                    <p className="font-body text-xs text-gray-500 line-clamp-1 mt-0.5">{m.message}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="font-body text-xs text-gray-400 hidden sm:block whitespace-nowrap">{fmtDate(m.created_at)}</span>
                  <button
                    onClick={e => { e.stopPropagation(); setDeleteId(m.id); }}
                    className="p-2 hover:bg-red-50 rounded-lg text-red-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-center py-16 text-gray-400 font-body bg-white rounded-2xl border border-gray-100">Nessun messaggio trovato</div>}
        </div>
      )}

      {/* Message Detail Modal — bottom sheet */}
      {selected && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white w-full sm:rounded-2xl sm:max-w-lg max-h-[90vh] flex flex-col rounded-t-2xl shadow-xl">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b flex-shrink-0">
              <h3 className="font-heading text-base font-bold truncate pr-4">Da: {selected.name}</h3>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 space-y-3">
              <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                <div><span className="font-body text-xs text-gray-400">Nome</span><p className="font-body text-sm font-semibold text-gray-900">{selected.name}</p></div>
                <div><span className="font-body text-xs text-gray-400">Email</span><a href={`mailto:${selected.email}`} className="block font-body text-sm text-primary truncate">{selected.email}</a></div>
                {selected.phone && <div><span className="font-body text-xs text-gray-400">Telefono</span><a href={`tel:${selected.phone}`} className="flex items-center gap-1 font-body text-sm text-primary"><Phone className="w-3.5 h-3.5" />{selected.phone}</a></div>}
                <div><span className="font-body text-xs text-gray-400">Data</span><p className="font-body text-xs text-gray-600">{fmtDate(selected.created_at)}</p></div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="font-body text-xs text-gray-400 mb-2">Messaggio</p>
                <p className="font-body text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
            </div>
            <div className="flex gap-2 px-4 sm:px-6 py-4 border-t bg-gray-50 sm:rounded-b-2xl flex-shrink-0">
              <a href={`mailto:${selected.email}`} className="flex-1">
                <Button className="w-full bg-primary hover:bg-primary-dark text-white rounded-xl text-sm">Rispondi via Email</Button>
              </a>
              <Button variant="outline" onClick={() => { setDeleteId(selected.id); setSelected(null); }} className="rounded-xl px-3 text-red-500 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-heading text-lg font-bold mb-2">Elimina Messaggio</h3>
            <p className="font-body text-gray-500 mb-6">Sei sicuro? Questa azione \u00e8 irreversibile.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteId(null)} className="flex-1 rounded-xl">Annulla</Button>
              <Button onClick={() => handleDelete(deleteId)} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl">Elimina</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
